import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Location } from '@angular/common';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { MegaHorizontalNavigationComponent } from 'app/layout/common/horizontal-mega-menu/mega-horizontal-navigation.component';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { HeaderSystemDatetimeComponent } from 'app/layout/common/header-system-datetime/header-system-datetime.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { filter, Subject, takeUntil } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { GlobalSearchService } from 'app/modules/platform/global-search/global-search.service';
import { UserService } from 'app/core/user/user.service';
import { StoreContextService, StoreOption } from 'app/core/store-context/store-context.service';
import { ModuleDropdownShortcutService } from 'app/shared/services/module-dropdown-shortcut.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AsyncPipe, NgIf } from '@angular/common';
import { LoadingSpinnerComponent } from 'app/modules/platform/mini-dashboard/components/loading-spinner/loading-spinner.component';
import {
  MINI_APP_DEFAULT_ROUTE,
  userHasMiniAppAdminRole,
} from 'app/core/auth/utils/mini-app-admin.utils';
import { WorkspaceTabsBarComponent } from 'app/layout/common/workspace-tabs/workspace-tabs-bar.component';
import { WorkspaceTabsService } from 'app/layout/common/workspace-tabs/workspace-tabs.service';
import { AppCommandsService } from 'app/layout/common/command-palette/shared/app-commands.service';
import { buildShellPaletteCommands } from 'app/layout/common/command-palette/app/app-commands';

@Component({
  selector: 'modern-layout',
  templateUrl: './modern.component.html',
  styleUrls: ['./modern.component.scss'],
  // encapsulation: ViewEncapsulation.Emulated,
  imports: [
    // FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MegaHorizontalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    FuseFullscreenComponent,
    // ShortcutsComponent,
    NotificationsComponent,
    HeaderSystemDatetimeComponent,
    UserComponent,
    RouterOutlet,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    MatTooltipModule,
    MatProgressBarModule,
    LoadingSpinnerComponent,
    WorkspaceTabsBarComponent,
    AsyncPipe,
    NgIf,
    //,
    //QuickChatComponent,
  ],
})
export class ModernLayoutComponent implements OnInit, OnDestroy {
  /** Toolbar fullscreen control; absent on onboarding routes, where the toolbar is not rendered. */
  @ViewChild(FuseFullscreenComponent) private _fullscreen?: FuseFullscreenComponent;
  private _unregisterAppCommands?: () => void;
  isScreenSmall: boolean | undefined;
  navigation: Navigation | undefined;
  dropdownControl = new FormControl('All');
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  dropdownOptions: string[] = [];
  selectedOption: string = '';
  filteredResults: string[] = [];
  searchTerm: string = '';
  stores$ = this._storeContext.stores$;
  selectedStoreId$ = this._storeContext.selectedStoreId$;
  isStoreSelectionLocked = false;
  isStoreSwitching = false;
  storeSwitchProgress = 0;
  private storeSwitchProgressTimer?: ReturnType<typeof setInterval>;

  get storeSwitchProgressRounded(): number {
    return Math.min(100, Math.round(this.storeSwitchProgress));
  }

  storeSelectorTooltip(locked: boolean): string {
    return locked ? 'Cannot change store during update' : 'Active Store';
  }

  storeNameById(stores: StoreOption[], storeId: string | null): string {
    return stores.find((store) => store.id === storeId)?.storeName ?? '';
  }

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _location: Location,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private searchState: GlobalSearchService,
    private _userService: UserService,
    private _storeContext: StoreContextService,
    private _moduleDropdownShortcuts: ModuleDropdownShortcutService,
    private _cdr: ChangeDetectorRef,
    private _workspaceTabs: WorkspaceTabsService,
    private _snackBar: MatSnackBar,
    private _appCommands: AppCommandsService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Check if user is Mini Tenant Admin or Mini Store Admin
   */
  get isMiniTenantAdmin(): boolean {
    const currentUser = this._userService.currentUser;
    if (!currentUser?.roles) return false;
    
    return currentUser.roles.some(role => {
      const roleKey = (role as any).roleKey?.toLowerCase() || '';
      const roleName = role.role?.toLowerCase() || '';
      return (
        roleKey === 'mini-tenant-admin' ||
        roleKey === 'mini-store-admin' ||
        roleName.includes('mini-tenant-admin') ||
        roleName.includes('mini-store-admin') ||
        roleName.replace(/\s+/g, '-') === 'mini-tenant-admin' ||
        roleName.replace(/\s+/g, '-') === 'mini-store-admin'
      );
    });
  }

  /**
   * Check if user is Tenant Admin (not mini tenant admin)
   */
  get isTenantAdmin(): boolean {
    const currentUser = this._userService.currentUser;
    if (!currentUser?.roles) return false;
    
    return currentUser.roles.some(role => {
      const roleKey = (role as any).roleKey?.toLowerCase() || '';
      const roleName = role.role?.toLowerCase() || '';
      const normalizedRoleName = roleName.replace(/\s+/g, '-');
      
      // Check for tenant-admin but exclude mini-tenant-admin
      const isTenantAdminRole = (
        roleKey === 'tenant-admin' ||
        (roleName.includes('tenant admin') && !roleName.includes('mini'))
      );
      
      const isNotMiniTenantAdmin = (
        roleKey !== 'mini-tenant-admin' &&
        !roleName.includes('mini-tenant-admin') &&
        normalizedRoleName !== 'mini-tenant-admin'
      );
      
      return isTenantAdminRole && isNotMiniTenantAdmin;
    });
  }

  /**
   * Check if user is Tenant Admin or Store Admin (but not mini versions)
   */
  get isTenantOrStoreAdmin(): boolean {
    const currentUser = this._userService.currentUser;
    if (!currentUser?.roles) return false;
    
    return currentUser.roles.some(role => {
      const roleKey = (role as any).roleKey?.toLowerCase() || '';
      const roleName = role.role?.toLowerCase() || '';
      const normalizedRoleName = roleName.replace(/\s+/g, '-');
      
      // Check for tenant-admin but exclude mini-tenant-admin
      const isTenantAdminRole = (
        roleKey === 'tenant-admin' ||
        (roleName.includes('tenant admin') && !roleName.includes('mini'))
      );
      
      const isNotMiniTenantAdmin = (
        roleKey !== 'mini-tenant-admin' &&
        !roleName.includes('mini-tenant-admin') &&
        normalizedRoleName !== 'mini-tenant-admin'
      );
      
      // Check for store-admin but exclude mini-store-admin
      const isStoreAdminRole = (
        roleKey === 'store-admin' ||
        (roleName.includes('store admin') && !roleName.includes('mini'))
      );
      
      const isNotMiniStoreAdmin = (
        roleKey !== 'mini-store-admin' &&
        !roleName.includes('mini-store-admin') &&
        normalizedRoleName !== 'mini-store-admin'
      );
      
      return (isTenantAdminRole && isNotMiniTenantAdmin) || 
             (isStoreAdminRole && isNotMiniStoreAdmin);
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._unregisterAppCommands = this._appCommands.register({
      build: () =>
        buildShellPaletteCommands({
          canGlobalSearch: !this.isOnboardingRoute && this.isTenantOrStoreAdmin,
          globalSearch: () => this.expandSearch(),
          canFullscreen: !!this._fullscreen && document.fullscreenEnabled,
          isFullscreen: !!document.fullscreenElement,
          toggleFullscreen: () => this._fullscreen?.toggleFullscreen(),
        }),
    });

    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

    // Serchbar Campartability with golbal search component
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((event: NavigationEnd) => {
        if (event.url.includes('/global-search')) {
          this.searchState.setExpanded(true);
        } else {
          this.searchState.setExpanded(false);
        }
      });

    this.searchState.dropdownOptions$.subscribe((options) => {
      this.dropdownOptions = options;
      if (!this.selectedOption) {
        this.selectedOption = options[0]; // default
      }
    });

    // Ensure store dropdown updates when stores load (fixes first-load visibility)
    this._storeContext.stores$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this._cdr.detectChanges());

    // Fallback: if user exists but stores are empty (e.g. after login), trigger load
    if (this.isTenantAdmin && this._storeContext.getCurrentStore() === null) {
      this._storeContext.loadStoresForCurrentUser();
    }

    this._storeContext.isSwitching$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((switching) => {
        this.isStoreSwitching = switching;
        if (switching) {
          this.startStoreSwitchProgress();
        } else {
          this.completeStoreSwitchProgress();
        }
        this._cdr.detectChanges();
      });

    this._storeContext.isStoreSelectionLocked$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((locked) => {
        this.isStoreSelectionLocked = locked;
        this._cdr.detectChanges();
      });

    // Open vertical nav drawer on mobile when module shortcut (Alt+M, etc.) is pressed
    this._moduleDropdownShortcuts.openDropdown$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        if (this.isScreenSmall) {
          const nav = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');
          if (nav?.open) {
            nav.open();
          } else {
            this.toggleNavigation('mainNavigation');
          }
        }
      });

    void this._bootstrapWorkspaceTabs();
  }

  private async _bootstrapWorkspaceTabs(): Promise<void> {
    this._workspaceTabs.beginBootstrap();
    await this._workspaceTabs.ensureShellBootstrapped();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this._unregisterAppCommands?.();
    this._unregisterAppCommands = undefined;
    this.clearStoreSwitchProgressTimer();
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  navigateToDashboard() {
    // Get current user
    const currentUser = this._userService.currentUser;
    
    // Check if user is super admin
    const isSuperAdmin = currentUser?.roles?.some(role => 
      role.role.toLowerCase().includes('super admin') || 
      role.role.toLowerCase().includes('superadmin')
    );
    
    // Check if user is mini app admin (mini tenant admin or mini store admin)
    const isMiniAppAdmin = userHasMiniAppAdminRole(currentUser?.roles);
    
    if (isSuperAdmin) {
      // Super Admin navigates to SaaS overview
      this._router.navigate(['/saas-overview']);
    } else if (isMiniAppAdmin) {
      // Mini app admins navigate to mini tenant dashboard
      this._router.navigate([MINI_APP_DEFAULT_ROUTE]);
    } else {
      // Other users navigate to regular dashboard
      this._router.navigate(['/dashboard']);
    }
  }

  toggleSearch() {
    this.searchState.setExpanded(!this.searchState.isExpanded);
  }

  onDropdownChange(option: string) {
    this.selectedOption = option;
    this.searchState.setSearchParams({ term: this.searchTerm, type: option });
  }

  async onStoreSelectionChange(storeId: string, select: MatSelect): Promise<void> {
    const currentId = this._storeContext.getActiveStoreId();
    const switched = await this._workspaceTabs.requestStoreSwitch(storeId);
    if (!switched) {
      select.value = currentId;
      this._cdr.markForCheck();
      return;
    }
    const storeName = this._storeContext.getCurrentStore()?.storeName;
    if (storeName) {
      this._snackBar.open(`Active store: ${storeName}`, 'Close', { duration: 2500 });
    }
  }

  fetchSuggestions(term: string) {
    this.searchTerm = term;
    this.searchState.setSearchParams({
      term: term,
      type: this.selectedOption,
    });
  }

  expandSearch() {
    this.searchState.setExpanded(true);
    setTimeout(() => {
      this._router.navigate(['/global-search']);
    }, 300);
  }

  get isExpanded(): boolean {
    const expanded = this.searchState.isExpanded;

    if (!expanded && this.searchTerm) {
      this.searchTerm = '';
      this.fetchSuggestions('');
    }
    return expanded;
  }

  /**
   * Check if current route is a sign-up onboarding route (not administration onboarding)
   * Only sign-up onboarding routes should hide the navigation menu
   */
  get isOnboardingRoute(): boolean {
    return this._router.url.startsWith('/on-boarding/sign-up-onboarding');
  }

  clearSearch(event: MouseEvent) {
    event.stopPropagation();
    this.searchTerm = '';
    this.fetchSuggestions('');
  }

  closeSearch(event: MouseEvent) {
    event.stopPropagation();
    this.searchState.setExpanded(false);
    this.searchTerm = '';
    this.fetchSuggestions('');
    this._location.back();
  }

  private startStoreSwitchProgress(): void {
    this.storeSwitchProgress = 0;
    this.clearStoreSwitchProgressTimer();
    this.storeSwitchProgressTimer = setInterval(() => {
      if (this.storeSwitchProgress < 88) {
        const headroom = 88 - this.storeSwitchProgress;
        const delta = Math.max(0.35, headroom * 0.055);
        this.storeSwitchProgress = Math.min(88, this.storeSwitchProgress + delta);
        this._cdr.detectChanges();
      }
    }, 90);
  }

  private completeStoreSwitchProgress(): void {
    this.clearStoreSwitchProgressTimer();
    this.storeSwitchProgress = 100;
  }

  private clearStoreSwitchProgressTimer(): void {
    if (this.storeSwitchProgressTimer != null) {
      clearInterval(this.storeSwitchProgressTimer);
      this.storeSwitchProgressTimer = undefined;
    }
  }
}
